<script setup>
import { computed, onBeforeUnmount, onMounted, ref } from 'vue';
import { useRoute } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import aiReviewsAPI from 'dashboard/api/aiReviews';
import Button from 'dashboard/components-next/button/Button.vue';
import PageHeader from '../components/PageHeader.vue';
import PagePagination from '../components/PagePagination.vue';
import StatusBadge from '../components/StatusBadge.vue';
import { formatDateTime, getApiErrorMessage } from '../utils';

const { t } = useI18n();
const route = useRoute();

const batch = ref(null);
const reviews = ref([]);
const reviewsMeta = ref({ current_page: 1, total_pages: 1, total_count: 0 });
const isLoading = ref(false);
const resultScope = ref('issues');
const issueTypeFilter = ref('');
const criterionFilter = ref('');
const agentFilter = ref('');
let pollTimer;
let loadBatch;

const analytics = computed(() => batch.value?.analytics || {});
const issueTypes = computed(() => analytics.value.issue_types || []);
const agents = computed(() => analytics.value.agents || []);
const criteria = computed(() =>
  [...(analytics.value.criteria || [])].sort(
    (left, right) =>
      right.failure_rate - left.failure_rate ||
      right.affected_conversations - left.affected_conversations
  )
);
const hasNonTerminalReviews = computed(() =>
  Boolean(batch.value && (batch.value.queued || batch.value.processing))
);
const requestFilters = computed(() => ({
  scope: resultScope.value,
  issue_type: issueTypeFilter.value || undefined,
  criterion_key: criterionFilter.value || undefined,
  agent_id: agentFilter.value || undefined,
}));
const activeFilterLabel = computed(() => {
  if (issueTypeFilter.value) {
    return issueTypes.value.find(item => item.key === issueTypeFilter.value)
      ?.name;
  }
  if (criterionFilter.value) {
    return criteria.value.find(item => item.key === criterionFilter.value)
      ?.name;
  }
  if (agentFilter.value === 'unattributed') {
    return t('AI_REVIEWS.BATCHES.UNATTRIBUTED');
  }
  if (agentFilter.value) {
    return agents.value.find(
      item => String(item.agent_id) === String(agentFilter.value)
    )?.agent_name;
  }
  return '';
});
const emptyResultsMessage = computed(() =>
  resultScope.value === 'all' && !activeFilterLabel.value
    ? t('AI_REVIEWS.BATCHES.EMPTY_RESULTS')
    : t('AI_REVIEWS.BATCHES.EMPTY_FILTERED_RESULTS')
);

const filterSummary = computed(() => {
  if (!batch.value) return [];
  const filters = batch.value.filters || {};
  const all = t('AI_REVIEWS.COMMON.ALL');
  const summary = [
    t('AI_REVIEWS.BATCHES.FILTER_ASSIGNEES', {
      value: filters.assignee_ids?.length
        ? filters.assignee_ids.join(', ')
        : all,
    }),
    t('AI_REVIEWS.BATCHES.FILTER_TEAMS', {
      value: filters.team_ids?.length ? filters.team_ids.join(', ') : all,
    }),
    t('AI_REVIEWS.BATCHES.FILTER_ACTIVITY', {
      from: formatDateTime(filters.activity_from),
      to: formatDateTime(filters.activity_to),
    }),
  ];
  if (filters.conversation_started_from) {
    summary.push(
      t('AI_REVIEWS.BATCHES.FILTER_CONVERSATION', {
        from: formatDateTime(filters.conversation_started_from),
        to: formatDateTime(filters.conversation_started_to),
      })
    );
  }
  if (filters.transferred_to_team_id) {
    summary.push(
      t('AI_REVIEWS.BATCHES.FILTER_TRANSFER_TEAM', {
        value: filters.transferred_to_team_id,
      })
    );
  }
  return summary;
});

const stopPolling = () => {
  window.clearInterval(pollTimer);
  pollTimer = undefined;
};

const syncPolling = () => {
  if (!hasNonTerminalReviews.value) {
    stopPolling();
    return;
  }
  if (!pollTimer) {
    pollTimer = window.setInterval(
      () => loadBatch(reviewsMeta.value.current_page, false),
      5000
    );
  }
};

loadBatch = async (
  page = reviewsMeta.value.current_page,
  showLoading = true
) => {
  try {
    if (showLoading) isLoading.value = true;
    const { data } = await aiReviewsAPI.getBatch(
      route.params.batchId,
      page,
      requestFilters.value
    );
    batch.value = data;
    reviews.value = data.reviews || [];
    reviewsMeta.value = data.reviews_meta || reviewsMeta.value;
    syncPolling();
  } catch (error) {
    useAlert(getApiErrorMessage(error, t('AI_REVIEWS.COMMON.API_ERROR')));
    stopPolling();
  } finally {
    isLoading.value = false;
  }
};

const applyPatternFilter = (kind, value) => {
  issueTypeFilter.value = kind === 'issue' ? value : '';
  criterionFilter.value = kind === 'criterion' ? value : '';
  agentFilter.value = kind === 'agent' ? String(value) : '';
  loadBatch(1);
};

const clearPatternFilter = () => {
  issueTypeFilter.value = '';
  criterionFilter.value = '';
  agentFilter.value = '';
  loadBatch(1);
};

const changeScope = () => loadBatch(1);
const valueOrFallback = value =>
  value === undefined || value === null || value === ''
    ? t('AI_REVIEWS.COMMON.NOT_AVAILABLE')
    : value;

onMounted(() => loadBatch(1));
onBeforeUnmount(stopPolling);
</script>

<template>
  <div v-if="isLoading && !batch" class="p-10 text-center text-n-slate-11">
    {{ t('AI_REVIEWS.COMMON.LOADING') }}
  </div>
  <template v-else-if="batch">
    <PageHeader
      :title="t('AI_REVIEWS.BATCHES.DETAIL_TITLE', { id: batch.id })"
      :description="t('AI_REVIEWS.BATCHES.DETAIL_DESCRIPTION')"
    >
      <template #actions>
        <StatusBadge :status="batch.status" />
        <Button
          :label="t('AI_REVIEWS.COMMON.REFRESH')"
          icon="i-lucide-refresh-cw"
          color="slate"
          variant="faded"
          :is-loading="isLoading"
          @click="loadBatch()"
        />
      </template>
    </PageHeader>

    <div class="grid gap-5 mb-6 lg:grid-cols-2">
      <section class="p-5 border rounded-xl border-n-weak bg-n-solid-2">
        <h3 class="mb-3 text-base font-semibold text-n-slate-12">
          {{ t('AI_REVIEWS.BATCHES.PROGRESS') }}
        </h3>
        <progress
          class="w-full h-3 mb-3 overflow-hidden rounded-full accent-n-brand"
          :value="batch.terminal_progress.terminal"
          :max="batch.terminal_progress.total || 1"
        />
        <p class="mb-4 text-sm text-n-slate-11">
          {{
            t('AI_REVIEWS.BATCHES.PROGRESS_SUMMARY', {
              terminal: batch.terminal_progress.terminal,
              total: batch.terminal_progress.total,
              percentage: batch.terminal_progress.percentage,
            })
          }}
        </p>
        <dl class="grid grid-cols-2 gap-3 text-sm sm:grid-cols-5">
          <div>
            <dt class="text-n-slate-11">{{ t('AI_REVIEWS.BATCHES.TOTAL') }}</dt>
            <dd class="mt-1 font-semibold text-n-slate-12">
              {{ batch.total }}
            </dd>
          </div>
          <div>
            <dt class="text-n-slate-11">
              {{ t('AI_REVIEWS.BATCHES.QUEUED') }}
            </dt>
            <dd class="mt-1 font-semibold text-n-slate-12">
              {{ batch.queued }}
            </dd>
          </div>
          <div>
            <dt class="text-n-slate-11">
              {{ t('AI_REVIEWS.BATCHES.PROCESSING') }}
            </dt>
            <dd class="mt-1 font-semibold text-n-slate-12">
              {{ batch.processing }}
            </dd>
          </div>
          <div>
            <dt class="text-n-slate-11">
              {{ t('AI_REVIEWS.BATCHES.COMPLETED') }}
            </dt>
            <dd class="mt-1 font-semibold text-n-slate-12">
              {{ batch.completed }}
            </dd>
          </div>
          <div>
            <dt class="text-n-slate-11">
              {{ t('AI_REVIEWS.BATCHES.FAILED') }}
            </dt>
            <dd class="mt-1 font-semibold text-n-slate-12">
              {{ batch.failed }}
            </dd>
          </div>
        </dl>
      </section>

      <section class="p-5 border rounded-xl border-n-weak bg-n-solid-2">
        <h3 class="mb-3 text-base font-semibold text-n-slate-12">
          {{ t('AI_REVIEWS.BATCHES.FILTERS') }}
        </h3>
        <ul class="grid gap-2 mb-4 text-sm text-n-slate-11">
          <li v-for="item in filterSummary" :key="item">{{ item }}</li>
        </ul>
        <p class="mb-1 text-sm text-n-slate-12">
          {{ batch.policy.name }} ·
          {{
            t('AI_REVIEWS.COMMON.VERSION', { version: batch.policy.version })
          }}
        </p>
        <p class="mb-0 text-sm text-n-slate-11">
          {{ batch.model_profile.name }} ·
          {{ batch.model_profile.model_identifier }}
        </p>
      </section>
    </div>

    <section class="mb-6">
      <div class="flex flex-wrap items-end justify-between gap-3 mb-3">
        <div>
          <h3 class="mb-1 text-base font-semibold text-n-slate-12">
            {{ t('AI_REVIEWS.BATCHES.EXCEPTION_OVERVIEW') }}
          </h3>
          <p class="mb-0 text-sm text-n-slate-11">
            {{ t('AI_REVIEWS.BATCHES.ANALYTICS_DENOMINATOR') }}
          </p>
        </div>
      </div>

      <dl class="grid gap-3 mb-5 sm:grid-cols-2 xl:grid-cols-4">
        <div class="p-4 border rounded-xl border-n-weak bg-n-solid-2">
          <dt class="text-sm text-n-slate-11">
            {{ t('AI_REVIEWS.BATCHES.REVIEWED_CONVERSATIONS') }}
          </dt>
          <dd class="mt-1 text-2xl font-semibold text-n-slate-12">
            {{ analytics.reviewed_conversations || 0 }}
          </dd>
        </div>
        <div class="p-4 border rounded-xl border-n-weak bg-n-solid-2">
          <dt class="text-sm text-n-slate-11">
            {{ t('AI_REVIEWS.BATCHES.CONVERSATIONS_WITH_ISSUES') }}
          </dt>
          <dd class="mt-1 text-2xl font-semibold text-n-ruby-11">
            {{ analytics.conversations_with_issues || 0 }}
          </dd>
        </div>
        <div class="p-4 border rounded-xl border-n-weak bg-n-solid-2">
          <dt class="text-sm text-n-slate-11">
            {{ t('AI_REVIEWS.BATCHES.ISSUE_RATE') }}
          </dt>
          <dd class="mt-1 text-2xl font-semibold text-n-ruby-11">
            {{ analytics.issue_rate || 0 }}%
          </dd>
        </div>
        <div class="p-4 border rounded-xl border-n-weak bg-n-solid-2">
          <dt class="text-sm text-n-slate-11">
            {{ t('AI_REVIEWS.BATCHES.TOTAL_ISSUES') }}
          </dt>
          <dd class="mt-1 text-2xl font-semibold text-n-slate-12">
            {{ analytics.total_issues || 0 }}
          </dd>
          <dd class="mt-1 text-xs text-n-slate-11">
            {{
              t('AI_REVIEWS.BATCHES.SEVERITY_SUMMARY', {
                critical: analytics.severity_counts?.critical || 0,
                major: analytics.severity_counts?.major || 0,
                minor: analytics.severity_counts?.minor || 0,
              })
            }}
          </dd>
        </div>
      </dl>

      <div class="grid gap-5 xl:grid-cols-2">
        <section class="p-5 border rounded-xl border-n-weak bg-n-solid-2">
          <h4 class="mb-3 text-sm font-semibold text-n-slate-12">
            {{ t('AI_REVIEWS.BATCHES.RECURRING_ISSUES') }}
          </h4>
          <p v-if="!issueTypes.length" class="mb-0 text-sm text-n-slate-11">
            {{ t('AI_REVIEWS.BATCHES.NO_DETECTED_ISSUES') }}
          </p>
          <ul v-else class="grid gap-3 mb-0">
            <li v-for="issueType in issueTypes" :key="issueType.key">
              <button
                type="button"
                class="w-full text-left group"
                @click="applyPatternFilter('issue', issueType.key)"
              >
                <span class="flex items-center justify-between gap-3 text-sm">
                  <span
                    class="font-medium text-n-slate-12 group-hover:text-n-brand"
                  >
                    {{ issueType.name }}
                  </span>
                  <span class="tabular-nums text-n-slate-11">
                    {{
                      t('AI_REVIEWS.BATCHES.ISSUE_OCCURRENCE', {
                        count: issueType.issue_count,
                        rate: issueType.conversation_rate,
                      })
                    }}
                  </span>
                </span>
                <progress
                  class="w-full h-2 mt-1 overflow-hidden rounded-full accent-n-ruby-9"
                  :value="issueType.affected_conversations"
                  :max="analytics.reviewed_conversations || 1"
                />
              </button>
            </li>
          </ul>
        </section>

        <section class="p-5 border rounded-xl border-n-weak bg-n-solid-2">
          <h4 class="mb-1 text-sm font-semibold text-n-slate-12">
            {{ t('AI_REVIEWS.BATCHES.AGENT_EXCEPTIONS') }}
          </h4>
          <p class="mb-3 text-xs text-n-slate-11">
            {{ t('AI_REVIEWS.BATCHES.AGENT_DENOMINATOR') }}
          </p>
          <div class="overflow-x-auto">
            <table class="w-full min-w-[560px] text-sm">
              <thead class="text-left text-n-slate-11">
                <tr>
                  <th class="pb-2 font-medium">
                    {{ t('AI_REVIEWS.BATCHES.AGENT') }}
                  </th>
                  <th class="pb-2 font-medium">
                    {{ t('AI_REVIEWS.BATCHES.REVIEWS') }}
                  </th>
                  <th class="pb-2 font-medium">
                    {{ t('AI_REVIEWS.BATCHES.WITH_ISSUES') }}
                  </th>
                  <th class="pb-2 font-medium">
                    {{ t('AI_REVIEWS.BATCHES.ISSUE_RATE') }}
                  </th>
                  <th class="pb-2 font-medium">
                    {{ t('AI_REVIEWS.BATCHES.MAIN_CAUSES') }}
                  </th>
                </tr>
              </thead>
              <tbody class="divide-y divide-n-weak">
                <tr v-for="agent in agents" :key="agent.agent_id">
                  <td class="py-2 pr-3">
                    <button
                      class="font-medium text-left text-n-brand hover:underline"
                      type="button"
                      @click="applyPatternFilter('agent', agent.agent_id)"
                    >
                      {{ agent.agent_name }}
                    </button>
                  </td>
                  <td class="py-2 pr-3 tabular-nums">
                    {{ agent.reviewed_conversations }}
                  </td>
                  <td class="py-2 pr-3 tabular-nums">
                    {{ agent.conversations_with_issues }}
                  </td>
                  <td class="py-2 pr-3 tabular-nums">
                    {{ agent.issue_rate }}%
                  </td>
                  <td class="py-2 text-n-slate-11">
                    {{
                      valueOrFallback(
                        agent.main_causes
                          .map(cause => `${cause.name} (${cause.count})`)
                          .join(', ')
                      )
                    }}
                  </td>
                </tr>
                <tr v-if="analytics.unattributed?.issue_count">
                  <td class="py-2 pr-3">
                    <button
                      class="font-medium text-left text-n-brand hover:underline"
                      type="button"
                      @click="applyPatternFilter('agent', 'unattributed')"
                    >
                      {{ t('AI_REVIEWS.BATCHES.UNATTRIBUTED') }}
                    </button>
                  </td>
                  <td class="py-2 pr-3 text-n-slate-11">
                    {{ t('AI_REVIEWS.COMMON.NOT_AVAILABLE') }}
                  </td>
                  <td class="py-2 pr-3 tabular-nums">
                    {{ analytics.unattributed.affected_conversations }}
                  </td>
                  <td class="py-2 pr-3 tabular-nums">
                    {{ analytics.unattributed.conversation_rate }}%
                  </td>
                  <td class="py-2 text-n-slate-11">
                    {{
                      analytics.unattributed.main_causes
                        .map(cause => `${cause.name} (${cause.count})`)
                        .join(', ')
                    }}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </section>
      </div>

      <details class="p-5 mt-5 border rounded-xl border-n-weak bg-n-solid-2">
        <summary class="text-sm font-semibold cursor-pointer text-n-slate-12">
          {{ t('AI_REVIEWS.BATCHES.CRITERION_PERFORMANCE') }}
        </summary>
        <p class="mt-3 mb-3 text-xs text-n-slate-11">
          {{ t('AI_REVIEWS.BATCHES.CRITERION_DENOMINATOR') }}
        </p>
        <div class="overflow-x-auto">
          <table class="w-full min-w-[720px] text-sm">
            <thead class="text-left text-n-slate-11">
              <tr>
                <th class="pb-2 font-medium">
                  {{ t('AI_REVIEWS.BATCHES.CRITERION') }}
                </th>
                <th class="pb-2 font-medium">
                  {{ t('AI_REVIEWS.BATCHES.APPLICABLE') }}
                </th>
                <th class="pb-2 font-medium">
                  {{ t('AI_REVIEWS.BATCHES.AFFECTED') }}
                </th>
                <th class="pb-2 font-medium">
                  {{ t('AI_REVIEWS.BATCHES.FAILURE_RATE') }}
                </th>
                <th class="pb-2 font-medium">
                  {{ t('AI_REVIEWS.BATCHES.MAIN_CAUSES') }}
                </th>
              </tr>
            </thead>
            <tbody class="divide-y divide-n-weak">
              <tr v-for="criterion in criteria" :key="criterion.key">
                <td class="py-2 pr-3">
                  <button
                    class="font-medium text-left text-n-brand hover:underline"
                    type="button"
                    @click="applyPatternFilter('criterion', criterion.key)"
                  >
                    {{ criterion.name }}
                  </button>
                </td>
                <td class="py-2 pr-3 tabular-nums">
                  {{ criterion.applicable_conversations }}
                </td>
                <td class="py-2 pr-3 tabular-nums">
                  {{ criterion.affected_conversations }}
                </td>
                <td class="py-2 pr-3 tabular-nums">
                  {{ criterion.failure_rate }}%
                </td>
                <td class="py-2 text-n-slate-11">
                  {{
                    valueOrFallback(
                      criterion.main_causes
                        .map(cause => `${cause.name} (${cause.count})`)
                        .join(', ')
                    )
                  }}
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </details>
    </section>

    <section>
      <div class="flex flex-wrap items-end justify-between gap-3 mb-3">
        <div>
          <h3 class="mb-1 text-base font-semibold text-n-slate-12">
            {{ t('AI_REVIEWS.BATCHES.RESULTS') }}
          </h3>
          <p v-if="activeFilterLabel" class="mb-0 text-sm text-n-slate-11">
            {{
              t('AI_REVIEWS.BATCHES.FILTERED_BY', { value: activeFilterLabel })
            }}
            <button
              type="button"
              class="ml-1 font-medium text-n-brand hover:underline"
              @click="clearPatternFilter"
            >
              {{ t('AI_REVIEWS.BATCHES.CLEAR_FILTER') }}
            </button>
          </p>
        </div>
        <label
          class="grid gap-1 text-sm text-n-slate-11"
          for="ai-review-result-scope"
        >
          {{ t('AI_REVIEWS.BATCHES.RESULT_SCOPE') }}
          <select
            id="ai-review-result-scope"
            v-model="resultScope"
            class="h-9 px-3 border rounded-lg border-n-weak bg-n-solid-2 text-n-slate-12"
            @change="changeScope"
          >
            <option value="issues">
              {{ t('AI_REVIEWS.BATCHES.ISSUES_ONLY') }}
            </option>
            <option value="all">
              {{ t('AI_REVIEWS.BATCHES.ALL_REVIEWS') }}
            </option>
          </select>
        </label>
      </div>
      <div
        v-if="!reviews.length"
        class="p-8 text-sm text-center border border-dashed rounded-xl border-n-weak text-n-slate-11"
      >
        {{ emptyResultsMessage }}
      </div>
      <template v-else>
        <div class="overflow-x-auto border rounded-xl border-n-weak">
          <table class="w-full min-w-[1100px] text-sm">
            <thead class="text-left bg-n-alpha-1 text-n-slate-11">
              <tr>
                <th class="p-3 font-medium">
                  {{ t('AI_REVIEWS.BATCHES.CONVERSATION') }}
                </th>
                <th class="p-3 font-medium">
                  {{ t('AI_REVIEWS.BATCHES.CITY') }}
                </th>
                <th class="p-3 font-medium">
                  {{ t('AI_REVIEWS.BATCHES.ASSIGNEE_AT_REVIEW') }}
                </th>
                <th class="p-3 font-medium">
                  {{ t('AI_REVIEWS.BATCHES.TEAM') }}
                </th>
                <th class="p-3 font-medium">
                  {{ t('AI_REVIEWS.BATCHES.ISSUES') }}
                </th>
                <th class="p-3 font-medium">
                  {{ t('AI_REVIEWS.BATCHES.ISSUE_TYPES') }}
                </th>
                <th class="p-3 font-medium">
                  {{ t('AI_REVIEWS.BATCHES.SCORE') }}
                </th>
                <th class="p-3 font-medium">
                  {{ t('AI_REVIEWS.BATCHES.STATUS') }}
                </th>
                <th class="p-3 font-medium">
                  {{ t('AI_REVIEWS.BATCHES.REVIEWED_AT') }}
                </th>
              </tr>
            </thead>
            <tbody class="divide-y divide-n-weak">
              <tr v-for="review in reviews" :key="review.id">
                <td class="p-3">
                  <RouterLink
                    class="font-medium text-n-brand hover:underline"
                    :aria-label="
                      t('AI_REVIEWS.BATCHES.OPEN_REVIEW', {
                        id: review.conversation_display_id,
                      })
                    "
                    :to="{
                      name: 'ai_review_show',
                      params: { batchId: batch.id, reviewId: review.id },
                    }"
                  >
                    #{{ review.conversation_display_id }}
                  </RouterLink>
                </td>
                <td class="p-3">{{ valueOrFallback(review.courier_city) }}</td>
                <td class="p-3">
                  {{ valueOrFallback(review.assignment_context.assignee_name) }}
                </td>
                <td class="p-3">
                  {{ valueOrFallback(review.assignment_context.team_name) }}
                </td>
                <td class="p-3 tabular-nums">{{ review.issue_count }}</td>
                <td class="p-3 text-n-slate-11">
                  {{ valueOrFallback(review.issue_types.join(', ')) }}
                </td>
                <td class="p-3 tabular-nums">
                  {{ valueOrFallback(review.overall_score) }}
                </td>
                <td class="p-3"><StatusBadge :status="review.status" /></td>
                <td class="p-3 text-n-slate-11">
                  {{ formatDateTime(review.reviewed_at) }}
                </td>
              </tr>
            </tbody>
          </table>
        </div>
        <PagePagination
          :current-page="reviewsMeta.current_page"
          :total-pages="reviewsMeta.total_pages"
          :total-count="reviewsMeta.total_count"
          @change="loadBatch($event)"
        />
      </template>
    </section>
  </template>
</template>
